import { useState, useEffect, useRef, useCallback } from "react";
import { Accessibility, X } from "lucide-react";
import { useTranslation } from "react-i18next";

// First-party accessibility widget (NOT a third-party overlay like AccessiBe /
// UserWay — those are discouraged by a11y experts and often make things worse).
// 3-mode model, aligned with the sibling apps on this machine (ebooklify, 2026-09-04):
//   📖 dyslexia · 🎨 color-blind · 🎯 focus.
// Mounted globally (App.js) so it's on EVERY page — public + dashboard, all 4
// surfaces (server-driven WebView). Settings persist in localStorage
// ("strongbot-a11y") and are applied via data-a11y-* on <html> (CSS in
// index.css). An inline script in public/index.html applies them before the
// first paint (zero flash). Strings come from i18next (a11y.*), 15 locales.

const STORAGE_KEY = "strongbot-a11y";

const DEFAULTS = { dyslexia: false, colorblind: "none", focus: false };

// Mirror of the pre-paint script in public/index.html — sets/removes the
// data-a11y-* attributes on <html> that index.css keys off of.
function apply(s) {
  if (typeof document === "undefined") return;
  const el = document.documentElement;
  if (s.dyslexia) el.setAttribute("data-a11y-dyslexia", "on");
  else el.removeAttribute("data-a11y-dyslexia");
  if (s.colorblind && s.colorblind !== "none")
    el.setAttribute("data-a11y-colorblind", s.colorblind);
  else el.removeAttribute("data-a11y-colorblind");
  if (s.focus) el.setAttribute("data-a11y-focus", "on");
  else el.removeAttribute("data-a11y-focus");
}

export default function AccessibilityWidget() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar" || i18n.language?.startsWith("ar");

  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(DEFAULTS);
  const triggerRef = useRef(null);
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);

  // Load persisted settings. <html> is already correct via the pre-paint
  // script; here we only sync the React panel state.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setSettings({ ...DEFAULTS, ...JSON.parse(raw) });
    } catch {
      /* localStorage blocked (private window / native) */
    }
  }, []);

  const persist = useCallback((next) => {
    setSettings(next);
    apply(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* localStorage blocked */
    }
  }, []);

  // Keyboard a11y (WCAG 2.1.2 / 2.4.3): Escape closes, focus returns to the
  // trigger, and Tab is trapped inside the panel while open.
  useEffect(() => {
    if (!open) return;
    const prevFocus = document.activeElement;
    // Move focus into the panel so keyboard users land inside it.
    closeBtnRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        return;
      }
      if (e.key === "Tab" && dialogRef.current) {
        const focusables = Array.from(
          dialogRef.current.querySelectorAll(
            'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])'
          )
        ).filter((el) => !el.disabled && el.offsetParent !== null);
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (prevFocus && typeof prevFocus.focus === "function") prevFocus.focus();
    };
  }, [open]);

  return (
    <>
      {/* Color-blindness correction filters (always in the DOM, out of flow).
          LMS correction matrices redistribute lost chromatic information toward
          the perceivable channels to improve color distinction. */}
      <svg
        aria-hidden="true"
        focusable="false"
        style={{
          position: "absolute",
          width: 0,
          height: 0,
          overflow: "hidden",
        }}
      >
        <defs>
          <filter id="a11y-cb-prot">
            <feColorMatrix
              type="matrix"
              values="0.567 0.433 0 0 0  0.558 0.442 0 0 0  0 0.242 0.758 0 0  0 0 0 1 0"
            />
          </filter>
          <filter id="a11y-cb-deut">
            <feColorMatrix
              type="matrix"
              values="0.625 0.375 0 0 0  0.7 0.3 0 0 0  0 0.3 0.7 0 0  0 0 0 1 0"
            />
          </filter>
          <filter id="a11y-cb-trit">
            <feColorMatrix
              type="matrix"
              values="0.95 0.05 0 0 0  0 0.433 0.567 0 0  0 0.475 0.525 0 0  0 0 0 1 0"
            />
          </filter>
        </defs>
      </svg>

      {/* Floating trigger — bottom-left to stay clear of the right-anchored
          feedback + language FABs. z-50 sits above them. On every page. */}
      {!open && (
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen(true)}
          aria-label={t("a11y.open")}
          title={t("a11y.open")}
          data-testid="a11y-open"
          className="fixed bottom-4 left-4 z-50 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg ring-1 ring-black/10 transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
        >
          <Accessibility className="h-6 w-6" aria-hidden="true" />
        </button>
      )}

      {open && (
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="false"
          aria-label={t("a11y.title")}
          dir={isRTL ? "rtl" : "ltr"}
          data-testid="a11y-panel"
          className="fixed bottom-4 left-4 z-50 w-[calc(100vw-2rem)] max-w-xs rounded-2xl bg-card p-4 text-card-foreground shadow-2xl ring-1 ring-border"
        >
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 text-base font-bold text-foreground">
              <Accessibility
                className="h-5 w-5 text-primary"
                aria-hidden="true"
              />
              {t("a11y.title")}
            </h2>
            <button
              ref={closeBtnRef}
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t("a11y.close")}
              className="rounded-lg p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          <div className="divide-y divide-border">
            {/* Dyslexia mode */}
            <label className="flex cursor-pointer items-center justify-between gap-3 py-3">
              <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                <span aria-hidden="true">📖</span>
                {t("a11y.dyslexia")}
              </span>
              <input
                type="checkbox"
                checked={settings.dyslexia}
                onChange={(e) =>
                  persist({ ...settings, dyslexia: e.target.checked })
                }
                data-testid="a11y-dyslexia"
                className="h-5 w-5 shrink-0 cursor-pointer rounded border-border accent-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
              />
            </label>

            {/* Color-blind mode */}
            <div className="flex items-center justify-between gap-3 py-3">
              <label
                htmlFor="a11y-colorblind"
                className="flex items-center gap-2 text-sm font-medium text-foreground"
              >
                <span aria-hidden="true">🎨</span>
                {t("a11y.colorblind")}
              </label>
              <select
                id="a11y-colorblind"
                value={settings.colorblind}
                onChange={(e) =>
                  persist({ ...settings, colorblind: e.target.value })
                }
                data-testid="a11y-colorblind"
                className="shrink-0 rounded-lg border border-border bg-background px-2 py-1.5 text-sm font-medium text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
              >
                <option value="none">{t("a11y.cbNone")}</option>
                <option value="prot">{t("a11y.cbProt")}</option>
                <option value="deut">{t("a11y.cbDeut")}</option>
                <option value="trit">{t("a11y.cbTrit")}</option>
              </select>
            </div>

            {/* Focus mode */}
            <label className="flex cursor-pointer items-center justify-between gap-3 py-3">
              <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                <span aria-hidden="true">🎯</span>
                {t("a11y.focus")}
              </span>
              <input
                type="checkbox"
                checked={settings.focus}
                onChange={(e) =>
                  persist({ ...settings, focus: e.target.checked })
                }
                data-testid="a11y-focus"
                className="h-5 w-5 shrink-0 cursor-pointer rounded border-border accent-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
              />
            </label>
          </div>

          <p className="mt-3 text-center text-xs text-muted-foreground">
            {t("a11y.saved")}
          </p>
        </div>
      )}
    </>
  );
}
